import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Avatar,
} from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getUsers, deleteUser } from '../../services/userService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';

import { User } from '../../types/user';
import { getRelativeFileUrl } from '../../services/relativeFilesService';

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estado de Paginação
  const [page, setPage] = useState<number>(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  /**
   * Função para buscar a lista de usuários.
   */
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
      setTotalItems(data.length);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      swal('Erro', 'Não foi possível buscar os usuários.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /**
   * Função para lidar com a deleção de um usuário.
   */
  const handleDelete = async (userId: string) => {
    const confirm = await swal({
      title: 'Tem certeza?',
      text: 'Você tem certeza que deseja deletar este usuário?',
      icon: 'warning',
      buttons: ['Cancelar', 'Deletar'],
      dangerMode: true,
    });

    if (confirm) {
      try {
        await deleteUser(userId);
        swal('Deletado!', 'Usuário deletado com sucesso.', 'success');
        fetchUsers();
      } catch (error) {
        console.error('Erro ao deletar usuário:', error);
        swal('Erro', 'Não foi possível deletar o usuário.', 'error');
      }
    }
  };

  /**
   * Função para lidar com mudança de página.
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Função para lidar com mudança de linhas por página.
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Calcula as linhas a serem exibidas na tabela com base na paginação.
   */
  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Cabeçalho com título e botão de adição */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Usuários</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/usuarios/novo')}
        >
          Adicionar Usuário
        </Button>
      </Box>

      {/* Estado de carregamento */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          {/* Tabela de Usuários */}
          <TableContainer component={Paper}>
            <Table aria-label="users table">
              <TableHead>
                <TableRow>
                  <TableCell>Foto</TableCell>
                  <TableCell>Nome Completo</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell>Administrador</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((userItem) => (
                  <TableRow key={userItem.id} hover>
                    <TableCell>
                      <Avatar
                        alt={userItem.full_name}
                        src={`${getRelativeFileUrl(userItem.profile_picture)}`}
                        sx={{ width: 64, height: 64, mb: 2 }}
                      />
                    </TableCell>
                    <TableCell>{userItem.full_name}</TableCell>
                    <TableCell>{userItem.email}</TableCell>
                    <TableCell>{userItem.phone_number}</TableCell>
                    <TableCell>{userItem.is_active ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>{userItem.is_system_admin ? 'Sim' : 'Não'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Visualizar">
                        <IconButton onClick={() => navigate(`/usuarios/${userItem.id}`)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => navigate(`/usuarios/${userItem.id}/editar`)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deletar">
                        <IconButton onClick={() => handleDelete(userItem.id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum usuário encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Controles de Paginação */}
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Registros por página"
          />
        </Box>
      )}
    </Box>
  );
};

export default UserList;